import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/**
 * Find the longest array in an array of arrays
 * @param arrays - Array of arrays
 * @returns The longest array
 */
export const getLongestArray = <T>(arrays: T[][]): T[] => {
	return arrays.reduce((max, current) =>
		current.length > max.length ? current : max,
	);
};

/**
 * Convert a hex color to an rgb object
 * @param inkColorHex - The hex color
 * @returns The rgb object
 */
export const hexToRgb = (inkColorHex: string) => {
	if (!inkColorHex) return { r: 255, g: 255, b: 255 };
	const normalized = inkColorHex.trim().replace(/^#/, "");
	if (normalized.length !== 6) return { r: 255, g: 255, b: 255 };
	const r = Number.parseInt(normalized.slice(0, 2), 16);
	const g = Number.parseInt(normalized.slice(2, 4), 16);
	const b = Number.parseInt(normalized.slice(4, 6), 16);
	if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b))
		return { r: 255, g: 255, b: 255 };
	return { r, g, b };
};

/**
 * Generate a SHA-256 hash for the given text
 * @param text - The text to generate a SHA-256 hash for
 * @returns The SHA-256 hash
 */
export async function hashContent(text: string): Promise<string> {
	const enc = new TextEncoder().encode(text);
	const hash = await crypto.subtle.digest("SHA-256", enc);
	return Array.from(new Uint8Array(hash))
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");
}
